import { PolymerElement, html } from 'polymer';

/**
 * @customElement
 * @polymer
 * @appliesMixin HIP.BehaviorElement
 * @appliesMixin HIP.BehaviorItemFocus
 * @appliesMixin HIP.BehaviorA11yGestures
 */
class ResultElement extends HIP.BehaviorItemFocus(
  HIP.BehaviorBrowserEvents(HIP.BehaviorA11yGestures(HIP.BehaviorElement(PolymerElement)))
) {
  static get is() {
    return 'hi-result';
  }

  static get template() {
    return html`
      <style is="custom-style" include="hi-theme"></style>
      <style>
        :host {
          font-size: var(--hi-font-size--medium);
          cursor: pointer;
          position: relative;
          background: transparent;
          line-height: var(--hi-size--large-x);
          padding: var(--hi-size--small-x) var(--hi-size--small-x);
          transition: border-color var(--hi-animation--speed-fast) var(--hi-animation--accel-fast),
            background var(--hi-animation--speed-normal) var(--hi-animation--accel-normal);
          border-top: var(--hi-size--pixel-1) solid var(--hi-color--transparent);
          border-bottom: var(--hi-size--pixel-1) solid var(--hi-color--transparent);
          display: list-item;
          list-style: none;
          outline: 0;
        }

        :host([disabled]) {
          color: var(--hi-color--disabled);
        }

        :host(:hover:not([disabled])) {
          background: var(--hi-color--gray-30);
        }

        :host([focused]:not([disabled])) {
          border-color: var(--hi-color--outline);
          background-color: var(--hi-color--theme-10);
        }

        :host(:active:not([disabled])) {
          border-color: var(--hi-color--gray-70);
          background: var(--hi-color--gray-20);
        }

        #letters strong {
          color: var(--hi-color--gray-100);
          font-size: 1.025em;
        }

        #letters span {
          color: var(--hi-color--gray-80);
        }
      </style>
      <div id="letters" class="text"></div>
    `;
  }

  static get properties() {
    return {
      filterRegExp: {
        type: Object
      },
      value: {
        type: String,
        reflectToAttribute: true
      },
      label: {
        type: String,
        reflectToAttribute: true
      }
    };
  }

  static get observers() {
    return ['__rebuildWordChanged(filterRegExp, label)'];
  }

  static get api() {
    return ['focus'];
  }

  static get gestureBindings() {
    return {
      tap: '_itemFocus__onTap'
    };
  }

  static get browserBindings() {
    return {
      mouseenter: '_itemFocus__onMouseEnter'
    };
  }

  static get autoBinding() {
    return {
      signal: 'focused-changed',
      field: 'focused'
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'option');
  }

  __rebuildWordChanged(_filterRegExp, _label) {
    if (!_label || _label.length <= 0) {
      return;
    }

    const parent = this.$.letters;
    const letters = this.__calculateLetters(_filterRegExp, _label);

    this.__rewriteTagContent(parent, letters, this.__selectTag);
  }

  /**
   * used to write tag sets and minimize DOM churn.
   *
   * @param {HTMLElement} parent the parent which owns the tags
   * @param {Array<String>} content a list of strings to write in to tags
   * @param {(<Number>) => <String>} tagBuilder creates tags based on the index of the child
   */
  __rewriteTagContent(parent, content, tagBuilder = (i) => 'div') {
    const children = parent.children || [];
    const contentSize = (content || []).length;
    const childrenSize = (children || []).length;

    if (contentSize < childrenSize) {
      for (let i = childrenSize - 1; i >= contentSize; i--) {
        parent.children[i].remove();
      }
    } else if (contentSize > childrenSize) {
      for (let i = childrenSize; i < contentSize; i++) {
        parent.appendChild(document.createElement(tagBuilder(i)));
      }
    }

    Array.from(parent.children).forEach((child, index) => (child.textContent = content[index]));
    return parent;
  }

  __calculateLetters(_filterRegExp, _label) {
    return (_label || '').split(_filterRegExp);
  }

  __selectTag(index) {
    return index % 2 === 1 ? 'strong' : 'span';
  }
}

HIP.factory.element(ResultElement);
